export const TOLERANCE_THRESHOLD = 0.1

const degreeToRadian = (degree) => {
    return degree * Math.PI / 180
}

export const isSinusEqualsToZero = (angle) => {
    return Math.abs(Math.round(Math.sin(degreeToRadian(angle)))) === 0
}

export class Position {

    constructor(x, y)
    {
        this.x = x
        this.y = y
    }

    permute()
    {
        return new Position(this.y, this.x)
    }

    move(vector)
    {
        return new Position(this.x + vector.x, this.y + vector.y)
    }

    duplicate()
    {
        return new Position(this.x, this.y)
    }

    createVector(position)
    {
        return new Vector(position.x - this.x, position.y - this.y)
    }

    convertToVector()
    {
        const position = new Position(0,0)

        return position.createVector(this)
    }

    equals(position)
    {
        return position.x === this.x && position.y === this.y
    }
}

export class Vector {

    constructor(x, y)
    {
        this.x = x
        this.y = y
    }

    add(vector)
    {
        return new Vector(this.x + vector.x, this.y + vector.y)
    }

    invert()
    {
        return new Vector(0 - this.x, 0 - this.y)
    }

    rotate(angle)
    {
        const radian = degreeToRadian(angle)
        const cos = Math.cos(radian)
        const sin = Math.sin(radian)

        return new Vector(
            this.x * cos - this.y * sin,
            this.x * sin + this.y * cos,
        )
    }

    scale(factor)
    {
        return new Vector(this.x * factor, this.y * factor)
    }

    unscale(factor)
    {
        if(factor === 0)
        {
            throw new Error('Cannot unscale by zero factor: division by zero')
        }

        return this.scale(1/factor)
    }

    equals(vector)
    {
        return vector.x === this.x && vector.y === this.y
    }

    inDeadZone()
    {
        return (Math.abs(this.x) <= TOLERANCE_THRESHOLD && Math.abs(this.y) <= TOLERANCE_THRESHOLD)
    }

    convertToPosition()
    {
        return new Position(this.x, this.y)
    }
}

export class Rectangle {

    constructor(width ,height, position)
    {
        this.width = width
        this.height = height
        this.position = new Position(0, 0)
        if(position !== undefined)
        {
            this.position = position
        }
    }

    static createFromElement(element)
    {
        const clientRect = element.getBoundingClientRect()

        return new Rectangle(
            clientRect.width,
            clientRect.height,
        )
    }

    rotate(angle)
    {
        let { width, height } = this

        if(! isSinusEqualsToZero(angle))
        {
            const oldWidth = width
            width = height
            height = oldWidth
        }

        return new Rectangle(width, height, this.position.duplicate())
    }

    scale(factor)
    {
        return new Rectangle(this.width * factor, this.height * factor, this.position.convertToVector().scale(factor).convertToPosition())
    }

    unscale(factor)
    {
        if(factor === 0)
        {
            throw new Error('Cannot unscale by zero factor: division by zero')
        }

        return this.scale(1/factor)
    }

    moveTo(position)
    {
        return new Rectangle(this.width, this.height, position)
    }

    move(vector)
    {
        return new Rectangle(this.width, this.height, this.position.move(vector))
    }

    reinitPos()
    {
        return new Rectangle(this.width, this.height)
    }

    equals(rectangle)
    {
        return this.width === rectangle.width && this.height === rectangle.height && this.position.equals(rectangle.position)
    }
}

export class Boundaries {

    constructor(imageRect)
    {
        const xBoundaries = {
            min: 0,
            max: imageRect.width,
        }

        const yBoundaries = {
            min: 0,
            max: imageRect.height
        }

        this.x = xBoundaries
        this.y = yBoundaries
    }

    computeConstrainedVector(vector)
    {
        return new Vector(
            this.keepWithinBoundary(vector.x, this.x),
            this.keepWithinBoundary(vector.y, this.y)
        )
    }

    keepWithinBoundary(value, boundaries) {

        if(value < boundaries.min)
        {
            return boundaries.min
        }

        if(value > boundaries.max)
        {
            return boundaries.max
        }

        return value
    }
}
